// Registry of live SessionTerms + wiring between the connection and the terminals/store.

import { addOpenLink, linkDone, sendLinkCaps } from '../ui/openLinkStore';
import { T, type ClosedSession, type Decoded, type MachineLayout, type ServerEvent, type Session, type Port } from '../net/protocol';
import { conn } from '../net/connection';
import { workspaceTakesConnection } from '../net/live';
import { quality } from '../net/quality';
import { store, toast } from '../store';
import { SessionTerm, currentTheme, termBaseSize, termFontSize } from './SessionTerm';
import { zoom } from './zoom';
import { machineCopied } from './drops';
import { onThemeChange } from '../ui/ds/theme';
import { NARROW_MQ } from '../ui/mobile';
import { notifyBrowser } from '../ui/notify';
import { heardMachineClock } from '../ui/time';
import { heardDriven } from '../ui/driven';

const terms = new Map<number, SessionTerm>();
/** Last grid size a pane fitted to, used for new sessions. */
let lastDims = { cols: 120, rows: 32 };

export function getTerm(sid: number): SessionTerm {
  let t = terms.get(sid);
  if (!t) {
    t = new SessionTerm(sid);
    terms.set(sid, t);
    const s = store.get().sessions.find((x) => x.id === sid);
    if (s?.status === 'exited') t.exitCode = s.exitCode ?? t.exitCode;
  }
  return t;
}

export function peekTerm(sid: number): SessionTerm | undefined {
  return terms.get(sid);
}

export function allTerms(): SessionTerm[] {
  return [...terms.values()];
}

export function rememberDims(cols: number, rows: number) {
  if (cols > 10 && rows > 3) lastDims = { cols, rows };
}

export function newSessionDims() {
  const f = store.get().focusedSession;
  const t = f !== null ? terms.get(f) : undefined;
  if (t && t.term.cols > 10) return { cols: t.term.cols, rows: t.term.rows };
  return lastDims;
}

function setSessions(sessions: Session[], closed: ClosedSession[] = []) {
  const prev = new Set(store.get().sessions.map((s) => s.id));
  store.set({ sessions, sessionsLoaded: true, closed });
  const alive = new Set(sessions.map((s) => s.id));
  for (const [sid, t] of terms) {
    if (!alive.has(sid)) {
      // Only drop terminals of sessions that disappeared (not ones created after this list).
      if (!prev.has(sid)) continue;
      t.dispose();
      terms.delete(sid);
      continue;
    }
    const s = sessions.find((x) => x.id === sid);
    if (s && s.status === 'exited' && t.exitCode === null) t.onExited(s.exitCode ?? 0);
  }
}

/** Tests (e2e): SNAPSHOT frames lost on the way, by session (window.__cmxLoseSnapshots, set
 *  before the page loads to lose its first ones). */
function loseSnapshot(sid: number): boolean {
  const lose = (window as { __cmxLoseSnapshots?: Record<number, number> }).__cmxLoseSnapshots;
  if (!lose?.[sid]) return false;
  lose[sid]--;
  return true;
}

function onSessionFrame(f: Decoded) {
  const t = terms.get(f.sid);
  if (!t) return; // not attached (e.g. frames racing a DETACH / kill)
  switch (f.type) {
    case T.OUTPUT:
      t.onOutput(f.offset, f.data);
      break;
    case T.SNAPSHOT:
      if (loseSnapshot(f.sid)) break;
      t.onSnapshot(f.offset, f.cols, f.rows, f.data);
      break;
    case T.EXITED:
      t.onExited(f.code);
      break;
    case T.INPUT_ACK:
      t.onInputAck(f.stream, f.seq);
      break;
  }
}

/** What the workspace does when `cmx detach` is typed in a session here (the "detach" event). */
let onLeave: (sid: number) => void = () => {};
export function setLeaveHandler(fn: (sid: number) => void) {
  onLeave = fn;
}

/** What follows the machine's workspaces (useWorkspaceSync): its state, whenever it changes, and each connection
 *  being ready (after the agent's hello). A state heard before it's set waits for it. */
export interface LayoutHandler {
  layout(l: MachineLayout): void;
  ready(): void;
}
let layoutHandler: LayoutHandler | null = null;
let unheardLayout: MachineLayout | null = null;
export function setLayoutHandler(h: LayoutHandler | null) {
  layoutHandler = h;
  if (h && unheardLayout) h.layout(unheardLayout);
  unheardLayout = null;
}

export function onEvent(e: ServerEvent) {
  switch (e.kind) {
    case 'layout':
      if (layoutHandler) layoutHandler.layout(e.layout);
      else unheardLayout = e.layout;
      break;
    case 'driven':
      heardDriven(e.session, e.by);
      break;
    case 'detach':
      onLeave(e.session);
      break;
    case 'sessions':
      heardMachineClock(e.now);
      setSessions(e.sessions ?? [], e.closed ?? []);
      break;
    case 'ports':
      store.set({ ports: e.ports ?? [] });
      break;
    case 'resync':
      peekTerm(e.session)?.expectResync();
      break;
    case 'desktop':
      store.set({ desktopRunning: !!e.running });
      break;
    case 'desktop-control':
      store.set({ desktopController: e.controller ?? '' });
      break;
    case 'net':
      quality.onReport(e);
      break;
    case 'machine-load': {
      const { kind: _, ...load } = e;
      // Clearing forgets Hide: the next time it comes back, it shows.
      store.set(load.state === 'ok' ? { machineLoad: load, loadHiddenAt: null } : { machineLoad: load });
      break;
    }
    case 'desktop-net': {
      const prev = store.get().desktopNet;
      store.set({ desktopNet: { mode: e.mode, policy: e.policy, fps: e.fps, scale: e.scale } });
      // Tell the user when the stream changes by itself (not when they chose it).
      if (prev && e.policy === 'auto' && prev.policy === 'auto' && store.get().desktopRunning) {
        if (e.scale < prev.scale)
          toast({ key: 'desktop-net', title: '⚠ The desktop’s picture is smaller for now', body: 'The link still can’t carry it at 30 fps; a lower resolution keeps it responsive. Your windows stay where they are.' }, 6000);
        else if (prev.mode !== e.mode && e.mode === 'low')
          toast({ key: 'desktop-net', title: '⚠ The desktop dropped to 30 fps', body: 'The link to this computer can’t carry it at full rate; fewer frames keep it responsive and text sharp.' }, 6000);
        else if (prev.mode !== e.mode) toast({ key: 'desktop-net', title: '✓ Desktop back to full frame rate', body: 'The link to this computer caught up.' }, 3000);
        else if (e.scale > prev.scale && e.scale === 1) toast({ key: 'desktop-net', title: '✓ Desktop back to full resolution', body: 'The link to this computer caught up.' }, 3000);
      }
      break;
    }
    case 'open-url':
      addOpenLink(e);
      break;
    case 'clipboard':
      machineCopied(e);
      break;
    case 'open-url-done':
      linkDone(e.id);
      break;
    case 'login-fields':
      store.set({ loginFields: e.fields?.length ? e : null });
      break;
    case 'notify': {
      const s = store.get();
      if (s.focusedSession === e.session && !document.hidden) break;
      const name = s.sessions.find((x) => x.id === e.session)?.name || 'Shell';
      const title = e.title || name;
      const body = e.body || (e.title ? name : 'needs you');
      // The toast is the canvas's: "<name> needs you", its folder, and what it asked.
      const ses = s.sessions.find((x) => x.id === e.session);
      toast({ title: e.title || `${name} needs you`, meta: ses?.cwd?.replace(/^\/(?:home|Users)\/[^/]+/, '~'), body: e.body || ses?.notice || '', session: e.session });
      notifyBrowser(title, body, e.session);
      break;
    }
  }
}

async function refreshAll() {
  try {
    const [l, p, d] = await Promise.all([
      conn.control<{ sessions: Session[]; closed?: ClosedSession[]; now?: string }>('list'),
      conn.control<{ ports: Port[] }>('ports').catch(() => ({ ports: store.get().ports })),
      conn.control<{ running: boolean }>('desktop.status').catch(() => ({ running: store.get().desktopRunning })),
    ]);
    heardMachineClock(l.now);
    setSessions(l.sessions ?? [], l.closed ?? []);
    store.set({ ports: p.ports ?? [], desktopRunning: !!d.running });
  } catch (e) {
    console.warn('cmx: initial sync failed', e);
  }
}

/** ⌘+ / ⌘− / ⌘0 in a session: its text alone gets bigger, smaller or back to the default, and only its terminal
 *  refits (and tells its program its new size). */
export function zoomSession(sid: number, by: -1 | 0 | 1) {
  zoom(sid, by, termBaseSize());
  peekTerm(sid)?.setFontSize(termFontSize(sid));
}

export function initTerminals() {
  workspaceTakesConnection();
  conn.setHandlers({
    onSessionFrame,
    onEvent,
    onConnected() {
      for (const t of terms.values()) if (t.attached) t.attach();
      void refreshAll();
    },
    onReady() {
      // After the re-ATTACH: input a dropped connection may have swallowed goes out again.
      for (const t of terms.values()) t.resendInput();
      sendLinkCaps(true);
      layoutHandler?.ready();
    },
  });

  // A hidden page's terminals aren't looked at (the machine counts who's looking); shown
  // again, each pane in sight draws whole at once.
  document.addEventListener('visibilitychange', () => {
    const shown = document.visibilityState === 'visible';
    for (const t of terms.values()) {
      t.sayViewing();
      if (shown) t.shown();
    }
  });
  // No terminal waits on an answer that may not come: each decides on this tick (SessionTerm.tick).
  setInterval(() => {
    const now = performance.now();
    for (const t of terms.values()) t.tick(now);
  }, 100);

  const retheme = () => {
    const th = currentTheme();
    for (const t of terms.values()) t.setTheme(th);
  };
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', retheme);
  // The workspace's appearance (light, dark, system) flips html[data-theme]; the tokens carry the colours.
  onThemeChange(retheme);

  // Phones get a smaller font; follow rotation / resizes across the breakpoint.
  matchMedia(NARROW_MQ).addEventListener('change', () => {
    for (const t of terms.values()) t.setFontSize(termFontSize(t.sid));
  });
  // The window moved to a display of another density (or the page was zoomed): cut the rows again.
  const density = () => {
    for (const t of terms.values()) t.setFontSize(termFontSize(t.sid));
    matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`).addEventListener('change', density, { once: true });
  };
  matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`).addEventListener('change', density, { once: true });
}
